import { computed } from "vue";
import { useRoute } from "vue-router";
import type { LocationQuery } from "vue-router";
import utils from "@/utils/utils";
import { rollbar } from "../../rollbar";

/** Vertriebsportal session cookie (base64 JSON with access_level, etc.) */
export const PORTAL_CURRENT_USER_COOKIE = "portal_current_user";

/** localStorage: once broker role was used, portal UI stays on until cleared. */
export const FROM_PORTAL_STORAGE_KEY = "covomo_from_portal";

/** Query override: ?role=broker|customer (aliases: ?rolle=vm|vn). */
export const FROM_PORTAL_QUERY = "role";
export const FROM_PORTAL_QUERY_ALIAS = "rolle";

function normalizeQueryValue(value: LocationQuery[string]): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (Array.isArray(value)) {
    const last = value[value.length - 1];
    return last === null || last === undefined ? undefined : last;
  }
  return value;
}

export function isBrokerRole(value: LocationQuery[string]): boolean {
  const v = normalizeQueryValue(value);
  if (v === undefined) return false;
  const s = String(v).toLowerCase();
  return s === "broker" || s === "vm";
}

export function isKundeRole(value: LocationQuery[string]): boolean {
  const v = normalizeQueryValue(value);
  if (v === undefined) return false;
  const s = String(v).toLowerCase();
  return s === "customer" || s === "vn";
}

export function hasPortalCookie(): boolean {
  return utils.hasCookie(PORTAL_CURRENT_USER_COOKIE);
}

export function getPersistedFromPortal(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(FROM_PORTAL_STORAGE_KEY) === "1";
  } catch (e) {
    rollbar?.error(e as Error, {
      context: "fromPortal:getPersistedFromPortal",
    });
    return false;
  }
}

export function setPersistedFromPortal(on: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (on) {
      window.localStorage.setItem(FROM_PORTAL_STORAGE_KEY, "1");
    } else {
      window.localStorage.removeItem(FROM_PORTAL_STORAGE_KEY);
    }
  } catch (e) {
    // ignore quota / private mode
    rollbar?.error(e as Error, {
      context: "fromPortal:setPersistedFromPortal",
    });
  }
}

/**
 * True when the user is in the Vertriebsportal broker flow.
 *
 * Priority:
 * 1) role/rolle query (broker|vm vs customer|vn)
 * 2) persisted localStorage flag
 * 3) portal cookie
 */
export function isFromPortal(query: LocationQuery): boolean {
  if (
    isBrokerRole(query[FROM_PORTAL_QUERY]) ||
    isBrokerRole(query[FROM_PORTAL_QUERY_ALIAS])
  ) {
    setPersistedFromPortal(true);
    return true;
  }
  if (
    isKundeRole(query[FROM_PORTAL_QUERY]) ||
    isKundeRole(query[FROM_PORTAL_QUERY_ALIAS])
  ) {
    setPersistedFromPortal(false);
    return false;
  }
  if (getPersistedFromPortal()) {
    return true;
  }
  return hasPortalCookie();
}

/**
 * Reactive portal/broker UI flag for any component under vue-router.
 */
export function useFromPortal() {
  const route = useRoute();
  return computed(() => isFromPortal(route.query));
}
